import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { WhatsAppContactWithDetails } from '@/types/whatsapp';
import { cn } from '@/lib/core/utils';

interface ChatListProps {
  contacts: WhatsAppContactWithDetails[];
  isLoading: boolean;
  selectedContactId: string | null;
  onSelectContact: (contactId: string | null) => void;
}

export function ChatList({
  contacts,
  isLoading,
  selectedContactId,
  onSelectContact,
}: ChatListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredContacts = contacts.filter(contact => {
    const query = searchQuery.toLowerCase();
    return (
      contact.phone_number.includes(query) ||
      (contact.profile_name?.toLowerCase().includes(query) ?? false)
    );
  });

  const getLastMessagePreview = (contact: WhatsAppContactWithDetails): string => {
    if (!contact.last_message) return 'No messages yet';

    const prefix = contact.last_message.direction === 'outbound' ? 'You: ' : '';
    const content = contact.last_message.content || `[${contact.last_message.message_type}]`;

    return prefix + (content.length > 40 ? content.substring(0, 40) + '...' : content);
  };

  const getTimeAgo = (contact: WhatsAppContactWithDetails): string => {
    const timestamp = contact.last_message?.sent_at || contact.created_at;
    return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b p-3">
        <h2 className="mb-2 font-semibold">Conversations</h2>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Contact list */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="space-y-2 p-2">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">
            {searchQuery ? 'No contacts found' : 'No conversations yet'}
          </div>
        ) : (
          <div className="divide-y">
            {filteredContacts.map(contact => (
              <button
                key={contact.id}
                onClick={() => onSelectContact(contact.id)}
                className={cn(
                  'flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-muted/50',
                  selectedContactId === contact.id && 'bg-muted'
                )}
              >
                {/* Avatar */}
                <div className={cn(
                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-lg font-medium',
                  contact.is_within_window
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                )}>
                  {(contact.profile_name || contact.phone_number)[0].toUpperCase()}
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">
                      {contact.profile_name || contact.phone_number}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {getTimeAgo(contact)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm text-muted-foreground">
                      {getLastMessagePreview(contact)}
                    </p>
                    <div className="flex shrink-0 items-center gap-1">
                      {!contact.is_within_window && (
                        <Clock className="h-3 w-3 text-orange-500" title="Outside 24h window" />
                      )}
                      {contact.unread_count > 0 && (
                        <Badge variant="default" className="h-5 min-w-[20px] px-1.5 text-xs">
                          {contact.unread_count}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Tags */}
                  {contact.tags && contact.tags.length > 0 && (
                    <div className="mt-1 flex gap-1">
                      {contact.tags.slice(0, 2).map(tag => (
                        <Badge key={tag} className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {contact.tags.length > 2 && (
                        <Badge className="text-xs">
                          +{contact.tags.length - 2}
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
